import type { ContentVisibility, EntityPluginContext } from "@brains/plugins";
import { normalizeTags } from "@brains/utils/string-utils";
import { agentEntitySchema, type AgentEntity } from "../schemas/agent";
import { skillEntitySchema, type SkillEntity } from "../schemas/skill";
import { AgentAdapter } from "../adapters/agent-adapter";
import { AGENT_ENTITY_TYPE, SKILL_ENTITY_TYPE } from "./constants";

export { normalizeTag, normalizeTags } from "@brains/utils/string-utils";

const agentAdapter = new AgentAdapter();

export interface TagVocabularyEntry {
  tag: string;
  count: number;
}

function sortVocabulary(a: TagVocabularyEntry, b: TagVocabularyEntry): number {
  if (b.count !== a.count) return b.count - a.count;
  return a.tag.localeCompare(b.tag);
}

export function buildTagVocabulary(
  skills: readonly SkillEntity[],
  agents: readonly AgentEntity[],
  opts: { minCount?: number; topN?: number } = {},
): TagVocabularyEntry[] {
  const minCount = opts.minCount ?? 1;
  const topN = opts.topN ?? 12;
  const counts = new Map<string, number>();

  const bump = (tags: string[]): void => {
    for (const tag of normalizeTags(tags)) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  };

  for (const skill of skills) bump(skill.metadata.tags);
  for (const agent of agents) {
    const body = agentAdapter.parseAgentContent(agent.content);
    bump(body.skills.flatMap((skill) => skill.tags));
  }

  return Array.from(counts.entries())
    .map(([tag, count]) => ({ tag, count }))
    .filter((entry) => entry.count >= minCount)
    .sort(sortVocabulary)
    .slice(0, topN);
}

/**
 * Takes only the entity service it reads from, not the whole plugin context:
 * a caller that has one — and a test that stubs one — needs nothing else.
 */
export async function collectTagVocabulary(
  context: Pick<EntityPluginContext, "entityService">,
  opts: {
    minCount?: number;
    topN?: number;
    visibilityScope?: ContentVisibility;
    includeSkills?: boolean;
  } = {},
): Promise<TagVocabularyEntry[]> {
  const visibilityScope: ContentVisibility = opts.visibilityScope ?? "public";
  const [skills, agents] = await Promise.all([
    opts.includeSkills === false
      ? Promise.resolve([])
      : context.entityService.listEntities(
          {
            entityType: SKILL_ENTITY_TYPE,
            options: { filter: { visibilityScope } },
          },
          skillEntitySchema,
        ),
    context.entityService.listEntities(
      {
        entityType: AGENT_ENTITY_TYPE,
        options: { filter: { visibilityScope } },
      },
      agentEntitySchema,
    ),
  ]);

  return buildTagVocabulary(skills, agents, opts);
}

export function formatVocabularyForPrompt(vocab: TagVocabularyEntry[]): string {
  if (vocab.length === 0) return "";

  return [
    "Current agent-directory tag vocabulary (reuse existing tags where they fit; propose new only when nothing does):",
    ...vocab.map(({ tag, count }) => `- ${tag} (${count})`),
  ].join("\n");
}
